import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@material-ui/core';

import WarningIcon from '@material-ui/icons/Warning';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Trans, useTranslation } from 'react-i18next';
import { LoadingOverlay } from '@pec/aion-ui-components/components/LoadingOverlay';
import { ReactComponent as HubOutlined } from 'images/Hub_Outlined.svg';
import { IPendingConnection } from 'interfaces';

interface Props {
  open: boolean;
  loading: boolean;
  handleChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleContinue(): void;
  handleSetUpLater(): void;
  option: PaymentOptions;
  incomingConnections: IPendingConnection[];
}

enum PaymentOptions {
  EnableAutoConnect = 'EnableAutoConnect',
  AddPaymentMethodOnly = 'AddPaymentMethodOnly'
}

export const ConsentDialog: React.FC<Props> = ({
  open,
  loading,
  handleChange,
  handleContinue,
  handleSetUpLater,
  option,
  incomingConnections
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const email = 'connections@veriforce.com';
  const getStrings = {
    title: t('subscriber:consentDialog.titlePrimary', 'Connecting With Hiring Clients Is Now Easier Than Ever'),
    subtitle: t(
      'subscriber:consentDialog.subtitlePrimary',
      'Our payment and connection terms have changed. Set up a preferred payment to request and accept connections.'
    ),
    labelAutoConnect: t('subscriber:consentDialog.labelAutoConnect', 'Enable Auto Connect + Add Preferred Payment'),
    labelAddMethodOnly: t('subscriber:consentDialog.labelAddMethodOnly', 'Add Preferred Payment Only'),
    subtextAutoConnect: t(
      'subscriber:consentDialog.subtextAutoConnect',
      'Automatically accept all connection requests from hiring clients with auto connect. Upon adding your preferred payment method, auto connect will be enabled.'
    ),
    subtextAddMethodOnly: t(
      'subscriber:consentDialog.subtextAddMethodOnly',
      'Get charged as you go. Manually accept incoming connections.'
    ),
    setUpLater: t('subscriber:consentDialog.setUpLater', 'Set up later'),
    continue: t('subscriber:consentDialog.continue', 'Continue'),
    connections: t('subscriber:consentDialog.connections', {
      connections: incomingConnections.length,
      defaultValue: '{{connections}} Pending Connections'
    })
  };

  return (
    <Dialog open={open} classes={{ paper: classes.dialogRoot }}>
      <HubOutlined className={classes.hubIcon} />
      <LoadingOverlay isLoading={loading}>
        <DialogTitle disableTypography className={classes.title}>
          {getStrings.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText className={classes.subtitle}>{getStrings.subtitle}</DialogContentText>
          <div>
            <FormControl component="fieldset">
              <RadioGroup name="paymentOption" value={option} onChange={handleChange}>
                <FormControlLabel
                  className={classes.label}
                  value={PaymentOptions.EnableAutoConnect}
                  control={<Radio color="primary" />}
                  label={getStrings.labelAutoConnect}
                />
                <DialogContentText className={classes.subtext}>{getStrings.subtextAutoConnect}</DialogContentText>
                {incomingConnections.length ? (
                  <div className={classes.connections}>
                    <WarningIcon />
                    <Typography>{getStrings.connections}</Typography>
                  </div>
                ) : null}
                <FormControlLabel
                  className={classes.label}
                  value={PaymentOptions.AddPaymentMethodOnly}
                  control={<Radio color="primary" />}
                  label={getStrings.labelAddMethodOnly}
                />
                <DialogContentText className={classes.subtext}>{getStrings.subtextAddMethodOnly}</DialogContentText>
              </RadioGroup>
            </FormControl>
            <DialogContentText>
              <Trans i18nKey="subscriber:consentDialog.contactsPrimary">
                Questions? Email{' '}
                <a href={`mailto:${email}`} className={classes.link}>
                  connections@veriforce.com.
                </a>
              </Trans>
            </DialogContentText>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSetUpLater} variant="text" color="primary">
            {getStrings.setUpLater}
          </Button>
          <Button variant="contained" color="primary" onClick={handleContinue}>
            {getStrings.continue}
          </Button>
        </DialogActions>
      </LoadingOverlay>
    </Dialog>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      fontSize: '18px',
      textAlign: 'center',
      width: '350px',
      margin: 'auto',
      fontWeight: 500
    },
    subtitle: {
      paddingLeft: '24px'
    },
    label: {
      fontWeight: 500,
      paddingLeft: '5px'
    },
    subtext: {
      paddingLeft: '40px'
    },
    link: {
      textDecoration: 'none',
      color: 'var(--primary-blue-six)'
    },
    hubIcon: {
      margin: 'auto'
    },
    dialogRoot: {
      padding: '16px 10px 10px'
    },
    connections: {
      display: 'flex',
      color: 'var(--green)',
      paddingLeft: '40px'
    }
  })
);
